import * as React from 'react'
import { data, redirect, Form, useActionData } from 'react-router'
import { z } from 'zod'
import { type Route } from './+types/register.ts'

const euchreRegistrationSchema = z.object({
	firstName: z.string().trim().min(1, 'First name is required'),
	lastName: z.string().trim().min(1, 'Last name is required'),
	email: z.string().trim().email('Please enter a valid email'),
	phone: z.string().trim().min(1, 'Phone is required'),
})

export const meta: Route.MetaFunction = () => [
	{ title: 'EBHS Events - 2023 Euchre Tournament' },
]

export async function action({ request }: Route.ActionArgs) {
	const { createEuchreRegistration } = await import(
		'#app/models/euchre-registration.server.ts'
	)
	const formData = await request.formData()
	const result = euchreRegistrationSchema.safeParse(
		Object.fromEntries(formData),
	)

	if (!result.success) {
		return data(
			{
				errors: result.error.flatten().fieldErrors,
				fields: Object.fromEntries(formData) as Record<string, string>,
			},
			{ status: 400 },
		)
	}

	await createEuchreRegistration(2023, result.data)

	return redirect('/events/euchre-tournament-2023/registration-confirmation')
}

export default function EuchreRegister2023() {
	const actionData = useActionData<typeof action>()

	const firstNameRef = React.useRef<HTMLInputElement>(null)
	const lastNameRef = React.useRef<HTMLInputElement>(null)
	const emailRef = React.useRef<HTMLInputElement>(null)
	const phoneRef = React.useRef<HTMLInputElement>(null)

	React.useEffect(() => {
		if (actionData?.errors?.firstName) {
			firstNameRef.current?.focus()
		} else if (actionData?.errors?.lastName) {
			lastNameRef.current?.focus()
		} else if (actionData?.errors?.email) {
			emailRef.current?.focus()
		} else if (actionData?.errors?.phone) {
			phoneRef.current?.focus()
		}
	}, [actionData])

	return (
		<>
			<header className="py-5">
				<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-bold leading-tight text-gray-900">
						Euchre Registration
					</h2>
					<p className="mt-3">
						If you are registering multiple people, please complete
						this form for each person being entered.
					</p>
				</div>
			</header>
			<div className="px-4 pt-4 pb-8">
				<div className="mx-auto max-w-6xl text-lg sm:px-6 lg:px-8">
					<Form method="post" className="space-y-4">
						<div>
							<label
								htmlFor="firstName"
								className="block text-sm font-medium text-gray-700"
							>
								First Name
							</label>
							<input
								type="text"
								id="firstName"
								name="firstName"
								ref={firstNameRef}
								required
								defaultValue={actionData?.fields?.firstName}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.firstName ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.firstName}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="lastName"
								className="block text-sm font-medium text-gray-700"
							>
								Last Name
							</label>
							<input
								type="text"
								id="lastName"
								name="lastName"
								ref={lastNameRef}
								required
								defaultValue={actionData?.fields?.lastName}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.lastName ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.lastName}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-700"
							>
								Email
							</label>
							<input
								type="email"
								id="email"
								name="email"
								ref={emailRef}
								required
								defaultValue={actionData?.fields?.email}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.email ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.email}
								</div>
							) : null}
						</div>

						<div>
							<label
								htmlFor="phone"
								className="block text-sm font-medium text-gray-700"
							>
								Phone
							</label>
							<input
								type="text"
								id="phone"
								name="phone"
								ref={phoneRef}
								required
								defaultValue={actionData?.fields?.phone}
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
							{actionData?.errors?.phone ? (
								<div className="pt-1 text-red-700">
									{actionData.errors.phone}
								</div>
							) : null}
						</div>

						<button className="rounded bg-blue-500 py-2 px-4 text-white hover:bg-blue-600 focus:bg-blue-400">
							Register
						</button>
					</Form>
				</div>
			</div>
		</>
	)
}
